/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, Inject, NgZone} from '@angular/core';
import {UIRouter} from '@uirouter/core';

import template from './mcp-authorize-page.component.html';
import {ApiConfig} from '../../../components/api/api';
import {
  AGENT_LEASE_HOURS,
  oauthErrorMessage,
  VALID_OAUTH_REDIRECT
} from './api-key-commons';
import {AdnHttpService} from '../../../components/util/adn-http.service';
import {
  AdnHttpSender,
  createAdnHttpResourceSender
} from '../../../components/util/create-adn-http-resource';
import {AuthHttpInterceptorService} from '../../../components/auth/auth-http-interceptor.service';
import {ApiErrorHandlerService} from '../../common/directives/error/api-error-handler.service';
import {LocalNetworkProfile} from '../../../components/session/local-network-profile';
import {LocalUserProfile} from '../../../components/session/local-user-profile';
import {Session} from '../../../components/session/session';
import {WINDOW} from '../../angular-platform-providers';

@Component({
  standalone: false,
  selector: 'adn-mcp-authorize-page',
  template,
  styles: [`
    .mcp-authorize-wrap {
      display: flex;
      justify-content: center;
      padding: 12vh 16px 40px;
    }
    .mcp-authorize {
      width: 100%;
      max-width: 480px;
      padding: 24px;
      background: #fff;
      border: 1px solid var(--adn-gl-border-default, #ddd);
      border-radius: 4px;
    }
    .mcp-authorize h1 {
      font-size: 20px;
      font-weight: 500;
      line-height: 1.3;
      margin: 0 0 8px;
      text-align: center;
    }
    .mcp-perms {
      list-style: none;
      margin: 16px 0 20px;
      padding: 0;
      border-top: 1px solid #e5e5e5;
    }
    .mcp-perms li {
      padding: 14px 0;
      border-bottom: 1px solid #e5e5e5;
    }
    .mcp-authorize-support {
      display: block;
      margin: 0 0 20px;
      font-weight: normal;
      color: #555;
    }
    .mcp-authorize-support input {
      margin-right: 8px;
    }
    .mcp-legal {
      margin: 0 0 24px;
      font-size: 12px;
      line-height: 1.5;
      color: #777;
    }
    .mcp-legal a,
    .mcp-authorize .alert a {
      color: inherit;
      text-decoration: underline;
    }
    .mcp-authorize .alert h2 {
      font-size: 16px;
      font-weight: 600;
      line-height: 1.3;
      margin: 0 0 8px;
    }
    .mcp-authorize .alert p {
      margin: 0 0 8px;
    }
    .mcp-authorize .alert p:last-child {
      margin-bottom: 0;
    }
    .mcp-actions {
      text-align: right;
    }
    .mcp-actions .btn + .btn {
      margin-left: 16px;
    }
  `]
})
export class McpAuthorizePageComponent {
  private readonly send: AdnHttpSender;

  oauthCodeChallenge: string | null = null;
  oauthCodeChallengeMethod = 'S256';
  oauthRedirectUri: string | null = null;
  oauthState = '';
  oauthClientId: string | null = null;
  agentOAuth = false;
  agentApp: string | null = null;
  oauthError: any = null;
  oauthErrorMessage: string | null = null;
  oauthRedirectHost: string | null = null;
  oauthRedirectIsLoopback = false;
  supportEligible = false;
  supportRequested = false;
  oauthResource: string | null = null;
  clientDeclaredSupport = false;
  authorizing = false;
  cancelled = false;
  networkName = 'Adnuntius';
  hasMultipleNetworks = false;
  twoFactorAuthEnabled = false;

  constructor(
    @Inject(WINDOW) private readonly window: Window,
    AdnHttp: AdnHttpService,
    authHttpInterceptor: AuthHttpInterceptorService,
    ApiErrorHandler: ApiErrorHandlerService,
    private readonly ngZone: NgZone,
    private readonly uiRouter: UIRouter,
    @Inject(LocalUserProfile) private readonly localUserProfile: any,
    @Inject(LocalNetworkProfile) private readonly localNetworkProfile: any
  ) {
    this.send = createAdnHttpResourceSender(AdnHttp, authHttpInterceptor, ApiErrorHandler);
    this.initOAuthParams();
    this.initSupportEligibility();
    this.networkName = this.localNetworkProfile.get('networkName') || 'Adnuntius';
    const networkIds = this.localUserProfile.get('networkTagIds');
    this.hasMultipleNetworks = Array.isArray(networkIds) && networkIds.length > 1;
    const twoFactorAuthType = this.localUserProfile.get('twoFactorAuthType');
    this.twoFactorAuthEnabled = twoFactorAuthType === 'APP' || twoFactorAuthType === 'EMAIL';
  }

  /** The client asked for support access this user cannot be granted. Minting anything here gives it a token its endpoint refuses, so refuse instead. */
  supportBlocked(): boolean {
    return this.clientDeclaredSupport && !this.supportEligible;
  }

  cancel(): void {
    this.cancelled = true;
    if (!this.oauthRedirectUri) {
      return;
    }
    setTimeout(() => this.redirectDenied(), 2000);
  }

  private redirectDenied(): void {
    if (!this.oauthRedirectUri) {
      return;
    }
    const sep = this.oauthRedirectUri.indexOf('?') === -1 ? '?' : '&';
    this.window.location.href = this.oauthRedirectUri + sep
      + 'error=access_denied'
      + '&state=' + encodeURIComponent(this.oauthState);
  }

  authorizeOAuth(): Promise<void> {
    if (this.supportBlocked() || this.authorizing || !this.agentOAuth || !this.twoFactorAuthEnabled) {
      return Promise.resolve();
    }
    this.oauthError = null;
    this.oauthErrorMessage = null;
    this.authorizing = true;
    const endpoint = ApiConfig.obtain().getVersionedUri('apikeys/authorize-code');
    const payload = {
      name: this.agentApp + ' on ' + (this.window.navigator.platform || 'this device'),
      scopes: ['ADVERTISING_QUERY', 'STATS_QUERY', 'PUBLISHING_QUERY'],
      expiry: new Date(Date.now() + AGENT_LEASE_HOURS * 3600 * 1000),
      readOnly: true
    };
    const params: Record<string, string> = {
      code_challenge: this.oauthCodeChallenge || '',
      code_challenge_method: this.oauthCodeChallengeMethod,
      redirect_uri: this.oauthRedirectUri || '',
      state: this.oauthState,
      client_id: this.oauthClientId || ''
    };
    if (this.clientDeclaredSupport || (!this.oauthResource && this.supportEligible && this.supportRequested)) {
      // The support audience: the server mints a support lease only when `resource` equals its own <issuer>/api/mcp/support string. Only ever chosen here when
      // the client declared no resource of its own — overriding a declared one would widen the lease past the request and leave the client a token its own
      // endpoint refuses.
      params.resource = ApiConfig.obtain().getUri('mcp/support');
    } else if (this.oauthResource) {
      // Echo back what the client asked for. Dropping it bound the lease to the bare endpoint, so a per-network connector authenticated and then failed the
      // audience check on every call. The server refuses a resource that is not one of its own, so passing it through cannot widen the lease.
      params.resource = this.oauthResource;
    }
    return this.send({
      method: 'POST',
      url: endpoint,
      params,
      body: payload
    }).then((data: any) => {
      data = data || {};
      const redirectUri = this.oauthRedirectUri || '';
      const sep = redirectUri.indexOf('?') === -1 ? '?' : '&';
      // iss is RFC 9207 issuer identification — the client checks it against the authorization server it began the flow with. The server advertises
      // authorization_response_iss_parameter_supported, so a strict client rejects an authorization response without it; only omit when the server didn't send one.
      const iss = data.iss ? '&iss=' + encodeURIComponent(data.iss) : '';
      this.window.location.href = redirectUri + sep
        + 'code=' + encodeURIComponent(data.code || '')
        + '&state=' + encodeURIComponent(data.state || this.oauthState)
        + iss;
    }, (response: any) => {
      const data = (response && response.data) || null;
      this.ngZone.run(() => {
        this.authorizing = false;
        this.oauthError = data || {};
        this.oauthErrorMessage = oauthErrorMessage(data);
      });
    });
  }

  // Mirrors the server's mint gate (ApiKeyResource): system-network role (canMasquerade), authenticator-app 2FA, not the super/internal
  // accounts, not masquerading. Display-only — the server refuses ineligible mints regardless. Masquerade is checked via both signals:
  // passkey sign-in leaves Session.masqueraderUsername empty while the profile still carries supportUserId.
  private initSupportEligibility(): void {
    const masquerading = !!Session.obtainData().masqueraderUsername || !!this.localUserProfile.get('supportUserId');
    this.supportEligible = this.agentOAuth
      && !!this.localUserProfile.get('canMasquerade')
      && this.localUserProfile.get('twoFactorAuthType') === 'APP'
      && !this.localUserProfile.isSpecialUser()
      && !masquerading;
    this.clientDeclaredSupport = this.agentOAuth && this.oauthResource === ApiConfig.obtain().getUri('mcp/support');
    // Only once it can actually be granted: a blocked request that still reads as requested shows the consent prose next to the refusal.
    if (this.clientDeclaredSupport && this.supportEligible) {
      this.supportRequested = true;
    }
  }

  private initOAuthParams(): void {
    const routeParams = this.uiRouter.globals.params;
    this.oauthCodeChallenge = routeParams.code_challenge || null;
    this.oauthCodeChallengeMethod = routeParams.code_challenge_method || 'S256';
    this.oauthRedirectUri = VALID_OAUTH_REDIRECT.test(routeParams.redirect_uri || '')
      ? routeParams.redirect_uri
      : null;
    this.oauthState = routeParams.state || '';
    this.oauthClientId = routeParams.client_id || null;
    this.oauthResource = routeParams.resource || null;
    this.agentOAuth = !!(this.oauthCodeChallenge && this.oauthRedirectUri && this.oauthClientId);
    this.agentApp = this.agentOAuth ? (routeParams.app || this.oauthClientId) : null;

    if (this.agentOAuth && this.oauthRedirectUri) {
      try {
        const parsed = new URL(this.oauthRedirectUri);
        this.oauthRedirectHost = parsed.host;
        this.oauthRedirectIsLoopback = (parsed.hostname === '127.0.0.1' || parsed.hostname === 'localhost');
      } catch (_error) {
        this.oauthRedirectHost = this.oauthRedirectUri;
        this.oauthRedirectIsLoopback = false;
      }
    }
  }
}
